import { useEffect, useState } from "react";
import api, { formatDate } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Megaphone, Plus, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

export default function AdminNotices() {
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/notices").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) { toast.error("Title and body required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/notices", { title: title.trim(), body: body.trim(), active: true });
      toast.success("Notice published");
      setTitle(""); setBody("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to publish");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (n) => {
    await api.patch(`/admin/notices/${n.id}`, { active: !n.active });
    toast.info(n.active ? "Notice hidden" : "Notice activated");
    load();
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete notice "${n.title}"?`)) return;
    await api.delete(`/admin/notices/${n.id}`);
    toast.success("Notice deleted");
    load();
  };

  return (
    <DashboardLayout title="Notices" subtitle={`${rows.filter((n) => n.active).length} active · ${rows.length} total`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Compose */}
        <form
          onSubmit={create}
          data-testid="notice-compose"
          className="lg:col-span-5 bg-white border border-navy/10 rounded-sm overflow-hidden self-start"
        >
          <div className="bg-navy text-white px-5 py-3 flex items-center gap-2 border-b border-gold/30">
            <Megaphone size={16} className="text-gold" />
            <span className="font-serif text-base">Publish a Notice</span>
          </div>
          <div className="p-5 space-y-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-navy/50 mb-1">Title</label>
              <input
                data-testid="notice-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                placeholder="e.g. Weekly closing this Sunday"
                className="w-full border border-navy/15 rounded-sm px-3 py-2 text-sm focus:border-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-navy/50 mb-1">Body</label>
              <textarea
                data-testid="notice-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                maxLength={500}
                placeholder="Short, friendly announcement visible on every distributor dashboard."
                className="w-full border border-navy/15 rounded-sm px-3 py-2 text-sm focus:border-gold focus:outline-none resize-none"
              />
              <div className="text-right text-[10px] text-navy/40 mt-0.5">{body.length}/500</div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="notice-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-olive text-white px-4 py-2.5 text-xs uppercase tracking-widest rounded-sm disabled:opacity-50 hover:bg-olive/90"
            >
              <Plus size={14} /> {busy ? "Publishing…" : "Publish"}
            </button>
          </div>
        </form>

        {/* List */}
        <div className="lg:col-span-7 bg-white border border-navy/10 rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-navy/10 flex items-center justify-between">
            <div className="font-serif text-base text-navy">All Notices</div>
            <div className="text-xs text-navy/50">{rows.length}</div>
          </div>
          {rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-navy/50">No notices yet. Compose your first one ←</div>
          ) : (
            <ul className="divide-y divide-navy/5" data-testid="notice-list">
              {rows.map((n) => (
                <li key={n.id} className="px-5 py-4 hover:bg-cream/30" data-testid={`notice-row-${n.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-navy">{n.title}</span>
                        {n.active ? (
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-olive/10 text-olive rounded-sm border border-olive/20">Live</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-navy/5 text-navy/40 rounded-sm border border-navy/10">Hidden</span>
                        )}
                      </div>
                      <p className="text-xs text-navy/70 whitespace-pre-wrap leading-relaxed">{n.body}</p>
                      <div className="text-[10px] text-navy/40 mt-2">{formatDate(n.created_at)}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => toggle(n)}
                        data-testid={`notice-toggle-${n.id}`}
                        title={n.active ? "Hide notice" : "Activate notice"}
                        className="p-1.5 text-navy/60 hover:text-navy hover:bg-cream rounded-sm"
                      >
                        {n.active ? <ToggleRight size={18} className="text-olive" /> : <ToggleLeft size={18} />}
                      </button>
                      <button
                        onClick={() => remove(n)}
                        data-testid={`notice-delete-${n.id}`}
                        title="Delete"
                        className="p-1.5 text-rose hover:bg-rose/5 rounded-sm"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
